import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { ShieldCheck, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import authapi from "@/services/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";

const CODE_LENGTH = 8;

const TwoFactorPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLogged, login } = useAuth();

  const userId = location.state?.userId;
  const fromPath = location.state?.from?.pathname || "/";

  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // userId yoksa login'e geri gönder
  useEffect(() => {
    if (!userId) {
      navigate("/login", { replace: true });
    }
  }, [userId, navigate]);

  // Input'a otomatik focus
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Hata mesajını kod değiştiğinde temizle
  useEffect(() => {
    setError(null);
  }, [code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== CODE_LENGTH) return;

    setSubmitting(true);
    setError(null);

    try {
      const { data: res } = await authapi.post("/Auth/two-factor", {
        userId,
        code,
      });

      const token = res.data?.Token;
      if (!token) {
        setError("Sunucudan geçerli bir token alınamadı.");
        return;
      }

      try {
        login(token);
      } catch (err) {
        if (err.message === "UNAUTHORIZED_ROLE") {
          setError("Geçersiz işlem denemesi. Bu sisteme erişim yetkiniz bulunmamaktadır.");
          return;
        }
        throw err;
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.response?.data?.ExceptionMessage ||
        "Doğrulama kodu geçersiz. Lütfen tekrar deneyin.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Zaten giriş yapmışsa yönlendir
  if (isLogged) {
    return <Navigate to={fromPath} replace />;
  }

  if (!userId) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-md bg-slate-900 text-white flex items-center justify-center font-black">
            A
          </div>
          <span className="font-heading font-black text-2xl tracking-tighter text-slate-900">
            ALTINGROSS
          </span>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h2
                  className="font-heading text-xl font-bold tracking-tight text-slate-900"
                  data-testid="twofactor-title"
                >
                  İki Adımlı Doğrulama
                </h2>
                <p className="text-sm text-slate-500">
                  E-posta adresinize gönderilen 8 haneli kodu girin.
                </p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              data-testid="twofactor-form"
            >
              <div className="space-y-2">
                <Input
                  ref={inputRef}
                  id="code"
                  type="text"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
                    setCode(val.slice(0, CODE_LENGTH).toUpperCase());
                  }}
                  placeholder="AB12CD34"
                  className="h-12 text-center text-lg font-mono tracking-[0.3em] font-semibold"
                  data-testid="twofactor-code-input"
                />
                <p className="text-xs text-slate-400 text-center">
                  {code.length} / {CODE_LENGTH} karakter
                </p>
              </div>

              {error && (
                <Alert variant="destructive" data-testid="twofactor-error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                disabled={submitting || code.length !== CODE_LENGTH}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                data-testid="twofactor-submit-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Doğrulanıyor...
                  </>
                ) : (
                  "Doğrula"
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full text-slate-500 hover:text-slate-700"
                onClick={() => navigate("/login", { replace: true })}
                data-testid="twofactor-back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Giriş sayfasına dön
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-xs text-center text-slate-400 mt-6">
          Kodu almadıysanız spam klasörünüzü kontrol edin.
        </p>
      </div>
    </div>
  );
};

export default TwoFactorPage;
